import type { ApiClient } from '@nly/api-client'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { apiClient } from '@/api'
import { formatApiError } from '@/utils/apiError'

type MetadataTree = Awaited<ReturnType<ApiClient['metadata']['tree']>>
type MetadataItemList = Awaited<ReturnType<ApiClient['metadata']['items']['list']>>
export type MetadataTreeNode = MetadataTree[number]
export type MetadataItem = MetadataItemList[number]

function compareItems(left: MetadataItem, right: MetadataItem) {
  return left.sortOrder - right.sortOrder
    || left.name.localeCompare(right.name)
    || left.id - right.id
}

export const useMetadataStore = defineStore('metadata', () => {
  const tree = ref<MetadataTree>([])
  const items = ref<MetadataItem[]>([])
  const selectedKey = ref('regions')
  const loading = ref(false)
  const itemsLoading = ref(false)
  const requestedTypeCode = ref<string | null>(null)
  const currentLoadedTypeCode = ref<string | null>(null)
  const mutationLoading = ref(false)
  const error = ref<string | null>(null)
  const itemsError = ref<string | null>(null)
  let treeGeneration = 0
  let itemsGeneration = 0
  let mutationRevision = 0
  let resetEpoch = 0

  const selectedNode = computed(() => {
    for (const group of tree.value) {
      if (group.kind === 'group') {
        const node = group.children?.find(child => child.key === selectedKey.value)
        if (node) {
          return node
        }
      }
      else if (group.key === selectedKey.value) {
        return group
      }
    }
    return undefined
  })

  async function loadTree() {
    const request = ++treeGeneration
    loading.value = true
    error.value = null
    try {
      const result = await apiClient.metadata.tree()
      if (request === treeGeneration) {
        tree.value = result
      }
      return result
    }
    catch (cause) {
      if (request === treeGeneration) {
        error.value = formatApiError(cause, '元数据目录加载失败')
      }
      throw cause
    }
    finally {
      if (request === treeGeneration) {
        loading.value = false
      }
    }
  }

  async function loadItems(typeCode: string) {
    const request = ++itemsGeneration
    const snapshotRevision = mutationRevision
    requestedTypeCode.value = typeCode
    itemsLoading.value = true
    itemsError.value = null
    try {
      const result = await apiClient.metadata.items.list({ typeCode, includeDisabled: true })
      if (request === itemsGeneration && snapshotRevision === mutationRevision) {
        items.value = result
        currentLoadedTypeCode.value = typeCode
      }
      return result
    }
    catch (cause) {
      if (request === itemsGeneration && snapshotRevision === mutationRevision) {
        itemsError.value = formatApiError(cause, '字典项加载失败')
      }
      throw cause
    }
    finally {
      if (request === itemsGeneration) {
        itemsLoading.value = false
      }
    }
  }

  function isTargetTypeActive(itemTypeCode: string) {
    if (requestedTypeCode.value) {
      return requestedTypeCode.value === itemTypeCode
    }
    if (currentLoadedTypeCode.value) {
      return currentLoadedTypeCode.value === itemTypeCode
    }
    return false
  }

  async function createItem(input: Parameters<typeof apiClient.metadata.items.create>[0]) {
    const epoch = resetEpoch
    mutationLoading.value = true
    try {
      const item = await apiClient.metadata.items.create(input)
      if (epoch === resetEpoch && isTargetTypeActive(item.typeCode)) {
        mutationRevision++
        items.value = [...items.value, item].sort(compareItems)
        currentLoadedTypeCode.value = item.typeCode
      }
      return item
    }
    finally {
      if (epoch === resetEpoch) {
        mutationLoading.value = false
      }
    }
  }

  async function updateItem(input: Parameters<typeof apiClient.metadata.items.update>[0]) {
    const epoch = resetEpoch
    mutationLoading.value = true
    try {
      const item = await apiClient.metadata.items.update(input)
      if (epoch === resetEpoch && isTargetTypeActive(item.typeCode)) {
        mutationRevision++
        items.value = items.value
          .map(current => current.id === item.id ? item : current)
          .sort(compareItems)
        currentLoadedTypeCode.value = item.typeCode
      }
      return item
    }
    finally {
      if (epoch === resetEpoch) {
        mutationLoading.value = false
      }
    }
  }

  async function setItemEnabled(input: Parameters<typeof apiClient.metadata.items.setEnabled>[0]) {
    const epoch = resetEpoch
    mutationLoading.value = true
    try {
      const item = await apiClient.metadata.items.setEnabled(input)
      if (epoch === resetEpoch && isTargetTypeActive(item.typeCode)) {
        mutationRevision++
        items.value = items.value.map(current => current.id === item.id ? item : current)
        currentLoadedTypeCode.value = item.typeCode
      }
      return item
    }
    finally {
      if (epoch === resetEpoch) {
        mutationLoading.value = false
      }
    }
  }

  function select(key: string) {
    selectedKey.value = key
  }

  function reset() {
    treeGeneration++
    itemsGeneration++
    mutationRevision++
    resetEpoch++
    tree.value = []
    items.value = []
    requestedTypeCode.value = null
    currentLoadedTypeCode.value = null
    selectedKey.value = 'regions'
    loading.value = false
    itemsLoading.value = false
    mutationLoading.value = false
    error.value = null
    itemsError.value = null
  }

  return {
    tree,
    items,
    currentLoadedTypeCode: computed(() => currentLoadedTypeCode.value),
    selectedKey,
    selectedNode,
    loading: computed(() => loading.value),
    itemsLoading: computed(() => itemsLoading.value),
    mutationLoading: computed(() => mutationLoading.value),
    error: computed(() => error.value),
    itemsError: computed(() => itemsError.value),
    loadTree,
    loadItems,
    createItem,
    updateItem,
    setItemEnabled,
    select,
    reset,
  }
})
